import {
    PhoneArrowUpRightIcon,
    PhoneIcon,
    PhoneXMarkIcon,
} from "@heroicons/react/24/outline";
import {
    ActivityParentEntityId,
    isPhoneNumberE164,
    toPhoneNumberE164,
} from "@sp-crm/core";
import { Call, Device } from "@twilio/voice-sdk";
import { DangerButton } from "components/ui/danger-button";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { useLogVoiceCallMutation, useRequestVoiceTokenMutation } from "generated/graphql";
import React, { useEffect, useReducer, useRef } from "react";
import { usePhoneLines } from "store/selectors/telecom";
import { inAdvancedMode } from "util/browser";
import { handleEvent } from "util/user-events";

interface PhoneCallProps {
    phoneNumber: string;
    entityId?: ActivityParentEntityId;
}

type CallState =
    | { type: "idle" }
    | { type: "calling" }
    | { type: "connected" }
    | { type: "ended" }
    | { type: "error"; message: string };

type CallAction =
    | { type: "startCall" }
    | { type: "callConnected" }
    | { type: "endCall" }
    | { type: "callEnded" }
    | { type: "error"; message: string }
    | { type: "reset" };

const callReducer = (state: CallState, action: CallAction): CallState => {
    switch (action.type) {
        case "startCall":
            return { type: "calling" };
        case "callConnected":
            return { type: "connected" };
        case "endCall":
            return { type: "ended" };
        case "callEnded":
            return { type: "ended" };
        case "error":
            return { type: "error", message: action.message };
        case "reset":
            return { type: "idle" };
        default:
            return state;
    }
};

const isAudioOutputSelectionError = (error: unknown): boolean => {
    if (!(error instanceof Error)) {
        return false;
    }

    if (
        error.name === "NotSupportedError" &&
        error.message.includes("Audio output selection")
    ) {
        return true;
    }

    if (error.name === "NotAllowedError" && error.message.includes("user gesture")) {
        return true;
    }

    return false;
};

export const PhoneCall: React.FC<PhoneCallProps> = props => {
    const { phoneNumber, entityId } = props;
    const [state, dispatch] = useReducer(callReducer, { type: "idle" });
    const deviceRef = useRef<Device | null>(null);
    const callRef = useRef<Call | null>(null);
    const isCallingRef = useRef(false);
    const phoneLines = usePhoneLines();
    const requestVoiceToken = useRequestVoiceTokenMutation();
    const logVoiceCall = useLogVoiceCallMutation();

    // Cleanup device on unmount
    useEffect(() => {
        return () => {
            cleanupDevice();
        };
    }, []);

    const cleanupDevice = () => {
        if (callRef.current) {
            callRef.current.disconnect();
            callRef.current = null;
        }
        if (deviceRef.current) {
            deviceRef.current.destroy();
            deviceRef.current = null;
        }
        isCallingRef.current = false;
    };

    const handleError = (error: unknown, context: string) => {
        if (isAudioOutputSelectionError(error)) {
            return;
        }
        cleanupDevice();
        const errorMessage =
            error instanceof Error
                ? error.message
                : String(error) || `Failed to ${context.toLowerCase()}`;
        dispatch({ type: "error", message: errorMessage });
    };

    const handleStartCall = async () => {
        if (
            !isPhoneNumberE164(phoneNumber) ||
            phoneLines.length === 0 ||
            isCallingRef.current
        ) {
            return;
        }

        try {
            isCallingRef.current = true;
            dispatch({ type: "startCall" });
            const phoneLine = phoneLines[0];

            handleEvent("initiate-voice-call", {
                phoneLineId: phoneLine.id,
            });

            if (entityId) {
                try {
                    await logVoiceCall.mutateAsync({
                        entityId,
                        phoneNumber: toPhoneNumberE164(phoneNumber),
                        phoneLineId: phoneLine.id,
                    });
                } catch (error) {
                    console.error("Failed to log voice call:", error);
                }
            }

            cleanupDevice();

            const token = await requestVoiceToken.mutateAsync({
                phoneLineId: phoneLine.id,
            });

            const device = new Device(token.requestVoiceToken.token, {
                logLevel: inAdvancedMode() ? "debug" : "silent",
            });

            deviceRef.current = device;

            device.on("error", error => {
                handleError(error, "Device error");
            });

            const call = await device.connect({
                params: {
                    To: toPhoneNumberE164(phoneNumber),
                    From: phoneLine.phoneNumber,
                },
            });

            callRef.current = call;

            call.on("accept", async () => {
                dispatch({ type: "callConnected" });
            });

            call.on("reject", () => {
                isCallingRef.current = false;
                dispatch({ type: "callEnded" });
            });

            call.on("disconnect", () => {
                isCallingRef.current = false;
                dispatch({ type: "callEnded" });
            });

            call.on("error", error => {
                handleError(error, "Call error");
            });

            device.on("disconnect", () => {
                isCallingRef.current = false;
                dispatch({ type: "callEnded" });
            });
        } catch (error) {
            handleError(error, "Failed to start call");
        }
    };

    const handleEndCall = () => {
        cleanupDevice();
        dispatch({ type: "endCall" });
    };

    const handleCallAgain = async () => {
        handleStartCall();
    };

    // Don't render if phone number is invalid or voice is disabled
    if (!isPhoneNumberE164(phoneNumber) || phoneLines.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col items-center space-y-6 p-6 bg-white rounded-lg shadow-lg max-w-sm mx-auto">
            {/* Contact Info */}
            <div className="text-center">
                <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PhoneIcon className="w-8 h-8 text-brand-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{phoneNumber}</h3>
            </div>

            {/* Call Status */}
            <div className="text-center">
                {state.type === "idle" && (
                    <p className="text-sm text-gray-600">Ready to call</p>
                )}
                {state.type === "calling" && (
                    <div className="flex items-center justify-center space-x-2">
                        <Spinner />
                        <p className="text-sm text-gray-600">Calling...</p>
                    </div>
                )}
                {state.type === "connected" && (
                    <div className="flex items-center justify-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <p className="text-sm text-green-600 font-medium">Connected</p>
                    </div>
                )}
                {state.type === "ended" && (
                    <p className="text-sm text-gray-600">Call ended</p>
                )}
            </div>

            <div className="flex flex-col space-y-3 w-full">
                {state.type === "idle" ? (
                    <PrimaryButton
                        onClick={handleStartCall}
                        disabled={phoneLines.length === 0 || isCallingRef.current}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneArrowUpRightIcon className="w-5 h-5" />
                        <span>Call</span>
                    </PrimaryButton>
                ) : null}

                {state.type === "calling" ? (
                    <DangerButton
                        onClick={handleEndCall}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneXMarkIcon className="w-5 h-5" />
                        <span>Cancel</span>
                    </DangerButton>
                ) : null}

                {state.type === "connected" ? (
                    <DangerButton
                        onClick={handleEndCall}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneXMarkIcon className="w-5 h-5" />
                        <span>Hang Up</span>
                    </DangerButton>
                ) : null}

                {state.type === "ended" ? (
                    <PrimaryButton
                        onClick={handleCallAgain}
                        disabled={isCallingRef.current}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneArrowUpRightIcon className="w-5 h-5" />
                        <span>Call Again</span>
                    </PrimaryButton>
                ) : null}

                {state.type === "error" ? (
                    <PrimaryButton
                        onClick={handleCallAgain}
                        disabled={isCallingRef.current}
                        className="flex items-center justify-center space-x-2 px-6 py-3 w-full">
                        <PhoneArrowUpRightIcon className="w-5 h-5" />
                        <span>Try Again</span>
                    </PrimaryButton>
                ) : null}
            </div>

            {state.type === "connected" ? (
                <div className="text-center">
                    <p className="text-xs text-gray-500">Call in progress</p>
                </div>
            ) : null}

            {state.type === "error" ? (
                <InlineBanner type="error" showIcon={true}>
                    {state.message}
                </InlineBanner>
            ) : null}
        </div>
    );
};
